import { createSelector } from 'reselect';
import _get from 'lodash/get';
import _keys from 'lodash/keys';
import { denormalize } from 'normalizr';
import { Schema } from 'normalizr';

export interface Entities {
  [id: string]: any;
}

interface Action {
  type: string;
  meta?: {
    entities?: {
      [name: string]: Entities;
    };
  };
}

interface LogoutAction {
  type: string;
  payload?: string[];
}

export type EntitiesAction = Action;
export interface EntitiesState {
  [name: string]: Entities;
}

const initialState: EntitiesState = {};

type NestedMap = { [key: string]: NestedMap };
const mergeDeep = <T extends NestedMap>(
  original: T,
  newObj: T,
  deepLevel: number,
): T => {
  if (!original) {
    return newObj;
  }

  if (deepLevel === 0) {
    return {
      ...original,
      ...newObj,
    };
  }

  const res = { ...original };
  _keys(newObj).forEach((key: keyof T) => {
    res[key] = mergeDeep(res[key], newObj[key], deepLevel - 1);
  });
  return res;
};

export const entitiesReducer = (
  state: EntitiesState = initialState,
  action: Action | LogoutAction,
) => {
  const entities = _get(action, 'meta.entities');
  if (entities) {
    return mergeDeep(state, entities, 2);
  }

  switch (action.type) {
    case 'AUTH.LOGOUT':
      if ('payload' in action) {
        const keptEntities = action.payload
          ? action.payload.reduce((acc: any, entityName: string) => {
              acc[entityName] = state[entityName];
              return acc;
            }, {})
          : {};
        return { ...initialState, ...keptEntities };
      }
      return initialState;
    default:
      return state;
  }
};

export const ENTITIES_MODULE_KEY: 'entities' = 'entities';
export type EntitiesGlobalState = { [ENTITIES_MODULE_KEY]: EntitiesState };
export type GlobalState = EntitiesGlobalState;

export const allEntitiesSelector = (state: GlobalState): EntitiesState =>
  state[ENTITIES_MODULE_KEY];

export const entitiesSelector = (
  state: GlobalState,
  entityName: string,
): Entities | undefined => allEntitiesSelector(state)[entityName];

export const plainEntityByIdSelector = <R>(
  state: GlobalState,
  entityName: string,
  id?: string,
): R | undefined => {
  const entities = entitiesSelector(state, entityName);
  if (!entities || !id) {
    return undefined;
  }
  return entities[id];
};

export const createAllIdsSelector = (entityName: string) => {
  return createSelector(
    (state: GlobalState) => entitiesSelector(state, entityName),
    (entities): string[] => {
      if (!entities) {
        return [];
      }
      return Object.keys(entities);
    },
  );
};

export const createAllEntitiesSelector = <R>(
  entityName: string,
  entitiesName: string[],
  schema: Schema,
  keepDeleted: boolean = false,
) => {
  const allIdsSelector = createAllIdsSelector(entityName);
  const severalEntitiesSelector = createSeveralEntitiesSelector(entitiesName);
  return createSelector(
    allIdsSelector,
    severalEntitiesSelector,
    (ids, entities): R[] => {
      if (!entities || !ids) {
        return [];
      }
      const res = denormalize(ids, schema, entities);
      if (keepDeleted || !res) {
        return res;
      }
      return res.filter((e: { deletedAt?: string }) => !e.deletedAt);
    },
  );
};

export const createSeveralEntitiesSelector = (entityNames: string[]) => {
  return createSelector(
    entityNames.map(
      (name) => (state: GlobalState) => entitiesSelector(state, name),
    ),
    (...entitiesArray) => {
      return entityNames.reduce((acc, name, index) => {
        return {
          ...acc,
          [name]: entitiesArray[index] || {},
        };
      }, {});
    },
  );
};

export const createDenormalizeSelector = <R>(
  schema: Schema,
  linkedEntites?: string[],
) => {
  return createSelector(
    (_: any, input: any) => input,
    linkedEntites
      ? createSeveralEntitiesSelector(linkedEntites)
      : allEntitiesSelector,
    (input, entities: any): R | undefined => {
      if (!input) {
        return undefined;
      }
      return denormalize(input, schema, entities);
    },
  ) as any;
};

export const createSingleEntitySelector = <S, R>(
  schema: Schema,
  idSelector: (state: S) => string | undefined,
  linkedEntites?: string[],
) => {
  const denormalizeSelector = createDenormalizeSelector<R>(
    schema,
    linkedEntites,
  );
  return (state: S & EntitiesGlobalState): R | undefined => {
    return denormalizeSelector(state, idSelector(state));
  };
};

export const createListFromStateIdsSelector = <S, R>(
  schema: Schema,
  idsSelector: (state: S) => string[],
  linkedEntites?: string[],
) => {
  const denormalizeSelector = createDenormalizeSelector<R[]>(
    schema,
    linkedEntites,
  );
  return (state: S & EntitiesGlobalState): R[] => {
    const res = denormalizeSelector(state, idsSelector(state));
    if (!res) {
      return [];
    }
    return res;
  };
};
